/// <amd-module name="Feed/Legacy/Protocols/Npm/Commands/NpmDeprecateCommand" />
import * as React from "react";
import * as ReactDOM from "react-dom";

import { IVssPageContext } from "VSS/Platform/Context";

import { Package, PackageVersion } from "Feed/Common/Contracts/Feed";
import * as NpmContracts from "Feed/Common/Protocols/Npm/Contracts/Npm";
import * as PackageResources from "Feed/Common/Resources";
import { IError } from "Feed/Common/Types/IError";
import { IGeneralDialogProps } from "Feed/Common/Types/IGeneralDialogProps";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { INpmDeprecateDialogProps, NpmDeprecateDialog } from "Feed/Legacy/Protocols/Npm/Commands/NpmDeprecateDialog";
import { NpmCiConstants } from "Feed/Legacy/Protocols/Npm/Constants/NpmConstants";
import { INpmDataService } from "Feed/Legacy/Protocols/Npm/NpmDataService";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class DeprecatePackagesCommand implements IPackageCommand {
    public readonly id: string;
    public readonly protocolCommand: ProtocolCommands;
    public readonly displayText: string;
    public readonly titleText: string;
    public readonly icon: string;
    public readonly disabled: boolean;
    public readonly actionMethod: () => void;

    constructor(id: string, displayText: string, dialogProps: IGeneralDialogProps, disabled: boolean) {
        this.id = id;
        this.protocolCommand = ProtocolCommands.Deprecate;
        this.displayText = displayText;
        this.icon = "bowtie-icon bowtie-math-minus";
        this.disabled = disabled;
        this.actionMethod = () => Actions.DialogOpenChanged.invoke(dialogProps);
    }
}

export class DeprecatePackageCommand implements IPackageCommand {
    public readonly id: string;
    public readonly protocolCommand: ProtocolCommands;
    public readonly displayText: string;
    public readonly titleText: string;
    public readonly icon: string;
    public readonly disabled: boolean;
    public readonly actionMethod: () => void;

    constructor(
        pageContext: IVssPageContext,
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean,
        disabled: boolean
    ) {
        this.id = "deprecate";
        this.protocolCommand = ProtocolCommands.Deprecate;
        this.displayText = PackageResources.PackageCommands_Deprecate;
        this.icon = "bowtie-icon bowtie-math-minus";
        this.disabled = disabled;
        this.actionMethod = () => {
            const containerClass = "deprecate-dialog-container";
            const elements = document.body.getElementsByClassName(containerClass);
            let container: Element;
            if (elements.length === 0) {
                container = document.createElement("div");
                container.classList.add(containerClass);
                document.body.appendChild(container);
            } else {
                container = elements[0];
            }

            const closeHandler = () => {
                if (container) {
                    ReactDOM.unmountComponentAtNode(container);
                }
            };

            const deprecateHandler = (deprecateMessage: string) => {
                const npmDataService = pageContext.getService<INpmDataService>("INpmDataService");

                CustomerIntelligenceHelper.publishEvent(pageContext, NpmCiConstants.DeprecatePackage, {
                    feedId: feed.id,
                    feedName: feed.name,
                    viewId: feed.view ? feed.view.id : undefined,
                    viewName: feed.view ? feed.view.name : undefined,
                    packageName: packageSummary.name,
                    packageVersion: packageVersion.version
                });

                const deprecatePackageVersionDetails = {
                    deprecateMessage,
                    views: null
                } as NpmContracts.PackageVersionDetails;

                return npmDataService
                    .updatePackage(
                        deprecatePackageVersionDetails,
                        feed,
                        packageSummary.normalizedName,
                        packageVersion.normalizedVersion
                    )
                    .then(
                        success => {
                            Actions.PackageVersionDeprecated.invoke({
                                message: deprecateMessage,
                                selectedVersion: packageVersion,
                                selectedPackages: viaPackageList ? [packageSummary] : null
                            });
                        },
                        err => {
                            if (err.status === 403) {
                                Actions.ErrorEncountered.invoke({
                                    message: PackageResources.Error_ErrorUserUnauthorized,
                                    details: err
                                } as IError);
                            } else {
                                Actions.ErrorEncountered.invoke({
                                    message:
                                        err.serverError && err.serverError.reason
                                            ? err.serverError.reason
                                            : PackageResources.Error_ErrorDeprecatingPackage,
                                    isCritical: true,
                                    details: err
                                } as IError);
                            }
                        }
                    );
            };

            const openDialog = () => {
                const deprecateDialog = React.createElement(NpmDeprecateDialog, {
                    packageSummary,
                    packageVersion,
                    onDismiss: closeHandler,
                    deprecatePackageDelegate: deprecateHandler
                } as INpmDeprecateDialogProps);
                ReactDOM.render(deprecateDialog, container);
            };

            if (packageVersion.protocolMetadata && packageVersion.protocolMetadata.data) {
                openDialog();
            } else {
                // When deprecating from the package list page, get the latest version.
                const feedsDataService: IFeedDataService = pageContext.getService<IFeedDataService>("IFeedDataService");
                const isDeleted = PermissionHelper.isDeleted(feed);
                const isListed = null;
                feedsDataService
                    .getPackageVersionAsync(feed, packageSummary.id, packageSummary.versions[0].id, isListed, isDeleted)
                    .then((version: PackageVersion) => {
                        packageVersion = version;
                        openDialog();
                    });
            }
        };
    }
}
